import { StatusGroup } from "./StatusGroup";

type TraceStatusMetadata = {
  label: string; // The text to show users
  group: StatusGroup; // which group in selection boxes to place the element
  tooltip?: string; // tooltip to display on hover
};

const statusMetadata = new Map<string, TraceStatusMetadata>([
  /*
    Helpers that select multiple statuses
  */
  [
    "selector_query_all_warnings",
    {
      label: "All Warnings",
      group: StatusGroup.Multiple,

      tooltip: "Include all events with a status categorized as a warning",
    },
  ],
  [
    "selector_query_all_errors",
    {
      label: "All Errors",
      group: StatusGroup.Multiple,

      tooltip: "Include all events with a status categorized as an error",
    },
  ],

  /*
  Success Results
  */
  [
    "ok",
    {
      label: "OK",
      group: StatusGroup.Success,

      tooltip:
        "This destination accepted our message and didn't report an error to us.",
    },
  ],
  [
    "transformation_skip",
    {
      label: "Skipped due to Transformation",
      group: StatusGroup.Success,

      tooltip: undefined,
    },
  ],
  [
    "dynamic_property_skip",
    {
      label: "Skipped due to property",
      group: StatusGroup.Success,

      tooltip: undefined,
    },
  ],
  [
    "delayed_processing",
    {
      label: "Delayed",
      group: StatusGroup.Success,

      tooltip:
        "A destination may not be ready to receive events. We're holding the event and will transmit when the destination is expected to be ready.",
    },
  ],

  /*
  Warnings
  */
  [
    "missing_required_property",
    {
      label: "Missing Required Property",
      group: StatusGroup.Warning,

      tooltip:
        "We did not send this request to the destination, because we determined that your payload is missing a required property.",
    },
  ],
  [
    "consent_not_granted",
    {
      label: "Consent Not Granted",
      group: StatusGroup.Success,

      tooltip:
        "We did not send this request to the destination because the user did not consent.",
    },
  ],
  [
    "downstream_warning",
    {
      label: "Downstream Warning",
      group: StatusGroup.Success,

      tooltip:
        "We received a response from the destination that in some cases may represent a problem.",
    },
  ],

  /*
  Errors
  */
  [
    "unknown",
    {
      label: "Unknown Error",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "transformation_error",
    {
      label: "Transformation Error",
      group: StatusGroup.Error,

      tooltip:
        "There was an error applying the transformation rule for this event. Please review transformation rules for your events at **TransformationError:** “There was an error applying the transformation rule for this event. Please review transformation rules for your events (https://app.freshpaint.io/transformations).",
    },
  ],
  [
    "timeout",
    {
      label: "Timeout",
      group: StatusGroup.Error,

      tooltip:
        "We attempted to send the event to the destination, but the request to the destination timed out.",
    },
  ],
  [
    "freshpaint_internal_error",
    {
      label: "Freshpaint Internal Error",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "network_error",
    {
      label: "Network Error",
      group: StatusGroup.Error,

      tooltip:
        "We encountered an error when trying to connect to the destination.",
    },
  ],
  [
    "request_too_large",
    {
      label: "Request Too Large",
      group: StatusGroup.Error,

      tooltip:
        "The request is too large and was dropped because the server will not accept the request.",
    },
  ],
  [
    "malformed_property",
    {
      label: "Malformed Property",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "token_expired",
    {
      label: "Token Expired",
      group: StatusGroup.Error,

      tooltip:
        "The token configured for this destination has expired. Please confirm you have a valid, up-to-date token value for this destination.",
    },
  ],
  [
    "downstream_error",
    {
      label: "Downstream Error",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "ambigious_user",
    {
      label: "Ambiguous User",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "unauthorized_user",
    {
      label: "Unauthorized User",
      group: StatusGroup.Error,

      tooltip:
        "Your user account is not authorized to perform this action. Please reach out to your account administrator to confirm your permissions settings.",
    },
  ],
  [
    "invalid_event_def",
    {
      label: "Invalid Event Definition",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "invalid_transformer",
    {
      label: "Invalid Transformation",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "invalid_connection_mode_server_side",
    {
      label: "Invalid Connection Mode Server Side",
      group: StatusGroup.Error,

      tooltip:
        "This action can only be completed when the destination’s connection mode is set to Server-side.",
    },
  ],
  [
    "not_configured",
    {
      label: "Not Configured",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "invalid_event_payload",
    {
      label: "Invalid Event Payload",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "bot_detected",
    {
      label: "Bot Detected",
      group: StatusGroup.Error,

      tooltip: "This event was classified as bot traffic and will be skipped.",
    },
  ],

  /*
  golang net/http/status.go all 3xx -> 5xx status codes
  */
  [
    "http_multiple_choices",
    {
      label: "Multiple Choices",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_moved_permanently",
    {
      label: "Moved Permanently",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  ["http_found", { label: "Found", group: StatusGroup.Error, tooltip: "" }],
  [
    "http_see_other",
    {
      label: "See Other",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_not_modified",
    {
      label: "Not Modified",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_use_proxy",
    {
      label: "Use Proxy",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_temporary_redirect",
    {
      label: "Temporary Redirect",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_permanent_redirect",
    {
      label: "Permanent Redirect",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_bad_request",
    {
      label: "Bad Request",
      group: StatusGroup.Error,

      tooltip:
        "We attempted to send the event to the destination, but the destination did not accept the HTTP request. Verify that your API key is correct.",
    },
  ],
  [
    "http_unauthorized",
    {
      label: "Unauthorized",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_payment_required",
    {
      label: "Payment Required",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_forbidden",
    {
      label: "Forbidden",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_not_found",
    {
      label: "Not Found",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_method_not_allowed",
    {
      label: "Method Not Allowed",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_not_acceptable",
    {
      label: "Not Acceptable",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_proxy_authentication_required",
    {
      label: "Proxy Authentication Required",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_request_timeout",
    {
      label: "Request Timeout",
      group: StatusGroup.Warning,

      tooltip: undefined,
    },
  ],
  [
    "http_conflict",
    {
      label: "Conflict",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  ["http_gone", { label: "Gone", group: StatusGroup.Error, tooltip: "" }],
  [
    "http_length_required",
    {
      label: "Length Required",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_precondition_failed",
    {
      label: "Precondition Failed",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_request_entity_too_large",
    {
      label: "Request Entity Too Large",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_request_uri_too_long",
    {
      label: "Request URI Too Long",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_unsupported_media_type",
    {
      label: "Unsupported Media Type",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_requested_range_not_satisfiable",
    {
      label: "Requested Range Not Satisfiable",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_expectation_failed",
    {
      label: "Expectation Failed",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_misdirected_request",
    {
      label: "Misdirected Request",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_unprocessable_entity",
    {
      label: "Unprocessable Entity",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_locked",
    {
      label: "Locked",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_failed_dependency",
    {
      label: "Failed Dependency",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_too_early",
    {
      label: "Too Early",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_upgrade_required",
    {
      label: "Upgrade Required",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_precondition_required",
    {
      label: "Precondition Required",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_too_many_requests",
    {
      label: "Too Many Requests",
      group: StatusGroup.Warning,

      tooltip: undefined,
    },
  ],
  [
    "http_request_header_fields_too_large",
    {
      label: "Request Header Fields Too Large",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_unavailable_for_legal_reasons",
    {
      label: "Unavailable For Legal Reasons",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_internal_server_error",
    {
      label: "Internal Server Error",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_not_implemented",
    {
      label: "Not Implemented",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_bad_gateway",
    {
      label: "Bad Gateway",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_service_unavailable",
    {
      label: "Service Unavailable",
      group: StatusGroup.Warning,

      tooltip: undefined,
    },
  ],
  [
    "http_gateway_timeout",
    {
      label: "Gateway Timeout",
      group: StatusGroup.Warning,

      tooltip: undefined,
    },
  ],
  [
    "http_http_version_not_supported",
    {
      label: "HTTP Version Not Supported",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_variant_also_negotiates",
    {
      label: "Variant Also Negotiates",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_insufficient_storage",
    {
      label: "Insufficient Storage",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_loop_detected",
    {
      label: "Loop Detected",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_not_extended",
    {
      label: "Not Extended",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
  [
    "http_network_authentication_required",
    {
      label: "Network Authentication Required",
      group: StatusGroup.Error,

      tooltip: undefined,
    },
  ],
]);

export default statusMetadata;
